import React, { useCallback, useEffect } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { useReactFlow } from '@xyflow/react';
import { useStore } from 'zustand';
import { useDiagramStore } from '../store/diagramStore';
import { hasAccess } from 'helpers/access';

interface MapToolbarProps {
  onAddService: () => void;
  onSaveLayout?: () => void; // OSS-NETVIZ-3: сохранить раскладку на сервер (undefined если проект не выбран)
  savingLayout?: boolean;
  onExportPng?: () => void; // OSS-NETVIZ-4: экспорт карты в PNG
}

const MapToolbar: React.FC<MapToolbarProps> = ({ onAddService, onSaveLayout, savingLayout, onExportPng }) => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const { activeFlowId, flows, setActiveFlow, resetPositions } = useDiagramStore();
  const activeFlow = flows.find(f => f.id === activeFlowId);

  // OSS-NETVIZ-1: undo/redo через zundo temporal. Счётчики истории — реактивны
  // (кнопки disabled когда нечего откатывать/повторять).
  const pastCount = useStore(useDiagramStore.temporal, (s) => s.pastStates.length);
  const futureCount = useStore(useDiagramStore.temporal, (s) => s.futureStates.length);
  const undo = useCallback(() => useDiagramStore.temporal.getState().undo(), []);
  const redo = useCallback(() => useDiagramStore.temporal.getState().redo(), []);

  // Хоткеи Ctrl/Cmd+Z (undo) и Ctrl/Cmd+Shift+Z или Ctrl+Y (redo).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return; // не перехватывать ввод в полях
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  return (
    <div
      style={{
        height: 48,
        background: '#fff',
        borderBottom: '1px solid #E0E0E0',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: 12,
        flexShrink: 0,
        zIndex: 20,
      }}
    >
      {/* Title */}
      <span style={{ fontSize: 15, fontWeight: 700, color: '#212121' }}>Service Map</span>
      <Badge bg="secondary" style={{ fontSize: 10 }}>Dev</Badge>

      {activeFlow && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: activeFlow.color, display: 'inline-block' }} />
          <span style={{ fontSize: 12, color: '#424242' }}>{activeFlow.name}</span>
          <button
            onClick={() => setActiveFlow(null)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9E9E9E', fontSize: 14, padding: '0 2px', lineHeight: 1,
            }}
            title="Clear flow"
          >
            ×
          </button>
        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* Undo/Redo (OSS-NETVIZ-1) */}
      <div style={{ display: 'flex', gap: 2 }}>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={undo}
          disabled={pastCount === 0}
          title="Отменить (Ctrl+Z)"
        >
          ↶
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={redo}
          disabled={futureCount === 0}
          title="Повторить (Ctrl+Shift+Z)"
        >
          ↷
        </Button>
      </div>

      {/* Actions */}
      <Button size="sm" variant="outline-primary" onClick={onAddService}>
        + Add Service
      </Button>

      {/* OSS-NETVIZ-3: сохранить раскладку на сервер (per-project), гейт serviceConnectEdit */}
      {onSaveLayout && hasAccess('serviceConnectEdit') && (
        <Button
          size="sm"
          variant="outline-success"
          onClick={onSaveLayout}
          disabled={savingLayout}
          title="Сохранить раскладку карты для текущего проекта"
        >
          {savingLayout ? 'Сохранение…' : 'Сохранить'}
        </Button>
      )}

      {/* Zoom controls */}
      <div style={{ display: 'flex', gap: 2 }}>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => zoomIn({ duration: 200 })}
          title="Zoom In"
        >
          +
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => zoomOut({ duration: 200 })}
          title="Zoom Out"
        >
          −
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => fitView({ duration: 400, padding: 0.05 })}
          title="Fit View"
        >
          ⊡
        </Button>
      </div>

      {/* OSS-NETVIZ-4: экспорт PNG (раскрывает адреса → гейт serviceConnectList) */}
      {onExportPng && hasAccess('serviceConnectList') && (
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={onExportPng}
          title="Экспорт карты в PNG"
        >
          PNG
        </Button>
      )}

      <Button
        size="sm"
        variant="outline-secondary"
        onClick={resetPositions}
        title="Reset layout"
      >
        Reset
      </Button>
    </div>
  );
};

export default MapToolbar;
